// src/utils/pdfGenerator.js
// Generates Shipping Label and Customer Invoice as printable HTML windows
// No external library needed - uses browser's print/save as PDF

// ── SHIPPING LABEL ────────────────────────────────────────────────
export function generateShippingLabel(order, merchantProfile) {
  const orderId    = order.id?.slice(-8).toUpperCase() || 'XXXXXXXX';
  const addr       = order.shippingAddress || {};
  const items      = order.items || [];
  const date       = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Shipping Label - ${orderId}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #111; background: white; }
    .page { width: 100mm; min-height: 150mm; border: 2px solid #111; padding: 10px; margin: 10px auto; }
    .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 8px; }
    .brand { font-size: 18px; font-weight: bold; color: #e11d48; }
    .order-id { font-size: 14px; font-weight: bold; background: #111; color: white; padding: 4px 8px; border-radius: 4px; }
    .section { margin-bottom: 10px; }
    .section-title { font-size: 9px; text-transform: uppercase; letter-spacing: 1px; color: #666; margin-bottom: 4px; font-weight: bold; }
    .name { font-size: 15px; font-weight: bold; margin-bottom: 2px; }
    .address { font-size: 12px; line-height: 1.6; }
    .phone { font-size: 12px; font-weight: bold; margin-top: 3px; }
    .divider { border-top: 1px dashed #999; margin: 8px 0; }
    .items-table { width: 100%; border-collapse: collapse; font-size: 11px; }
    .items-table th { background: #f3f3f3; padding: 4px 6px; text-align: left; border: 1px solid #ddd; }
    .items-table td { padding: 4px 6px; border: 1px solid #ddd; }
    .payment-badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-weight: bold; font-size: 11px; }
    .paid  { background: #dcfce7; color: #166534; }
    .cod   { background: #fef9c3; color: #854d0e; }
    .footer { border-top: 1px solid #ddd; padding-top: 6px; margin-top: 8px; font-size: 10px; color: #666; text-align: center; }
    .barcode-area { text-align: center; margin: 8px 0; }
    .barcode-text { font-family: monospace; font-size: 20px; letter-spacing: 4px; font-weight: bold; border: 1px solid #ddd; padding: 4px 8px; display: inline-block; }
    @media print {
      body { margin: 0; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="text-align:center; padding: 10px; background: #f0f0f0;">
    <button onclick="window.print()" style="background:#e11d48; color:white; border:none; padding:8px 20px; border-radius:6px; cursor:pointer; font-size:14px; margin-right:8px;">🖨️ Print Label</button>
    <button onclick="window.close()" style="background:#666; color:white; border:none; padding:8px 20px; border-radius:6px; cursor:pointer; font-size:14px;">✕ Close</button>
  </div>

  <div class="page">
    <div class="header">
      <div class="brand">💕 CoupleCart</div>
      <div class="order-id">#${orderId}</div>
    </div>

    <!-- TO: Customer address -->
    <div class="section">
      <div class="section-title">📦 Deliver To</div>
      <div class="name">${addr.name || '—'}</div>
      <div class="address">
        ${addr.address || ''}<br/>
        ${addr.city || ''}, ${addr.state || ''} — ${addr.pincode || ''}
      </div>
      <div class="phone">📞 ${addr.phone || '—'}</div>
    </div>

    <div class="divider"></div>

    <!-- FROM: Merchant address -->
    <div class="section">
      <div class="section-title">📬 Shipped From</div>
      <div class="name">${merchantProfile?.shopName || 'CoupleCart Seller'}</div>
      <div class="address">
        ${merchantProfile?.pickupAddress || ''}<br/>
        ${merchantProfile?.pickupCity || ''}, ${merchantProfile?.pickupState || ''} — ${merchantProfile?.pickupPincode || ''}
      </div>
      <div class="phone">📞 ${merchantProfile?.pickupPhone || merchantProfile?.phone || '—'}</div>
    </div>

    <div class="divider"></div>

    <!-- Items -->
    <div class="section">
      <div class="section-title">🛍️ Items</div>
      <table class="items-table">
        <thead>
          <tr><th>Product</th><th>Qty</th><th>Price</th></tr>
        </thead>
        <tbody>
          ${items.map((item) => `
            <tr>
              <td>${item.name}</td>
              <td style="text-align:center">${item.quantity}</td>
              <td>₹${(item.price * item.quantity).toLocaleString('en-IN')}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>

    <!-- Payment + Date -->
    <div style="display:flex; justify-content:space-between; align-items:center; margin-top:8px;">
      <div>
        <span class="section-title">Payment: </span>
        <span class="payment-badge ${order.paymentStatus === 'paid' ? 'paid' : 'cod'}">
          ${order.paymentStatus === 'paid' ? '✓ PREPAID' : '💵 COD'}
        </span>
      </div>
      <div style="font-size:11px; color:#666;">Date: ${date}</div>
    </div>

    <!-- Barcode / Order ID visual -->
    <div class="barcode-area" style="margin-top:10px;">
      <div class="barcode-text">${order.id?.toUpperCase() || orderId}</div>
      <div style="font-size:10px; color:#666; margin-top:3px;">Order ID</div>
    </div>

    ${order.awbCode ? `<div class="barcode-area"><div class="barcode-text">${order.awbCode}</div><div style="font-size:10px;color:#666;margin-top:3px;">AWB / Tracking</div></div>` : ''}

    <div class="footer">
      CoupleCart — couplecart.in | Gifts crafted with love 💕<br/>
      For support: hello@couplecart.in
    </div>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=500,height=700');
  win.document.write(html);
  win.document.close();
}


// ── CUSTOMER INVOICE ──────────────────────────────────────────────
export function generateInvoice(order) {
  const orderId  = order.id?.slice(-8).toUpperCase() || 'XXXXXXXX';
  const addr     = order.shippingAddress || {};
  const items    = order.items || [];
  const subtotal = order.subtotal || order.total || 0;
  const delivery = order.deliveryFee || 0;
  const total    = order.total || 0;
  const date     = order.createdAt?.toDate?.()?.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) || new Date().toLocaleDateString('en-IN');

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Invoice - ${orderId}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 13px; color: #222; background: white; }
    .invoice { max-width: 700px; margin: 20px auto; padding: 40px; border: 1px solid #e5e7eb; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; }
    .brand-name { font-size: 28px; font-weight: 900; color: #e11d48; }
    .brand-tagline { font-size: 11px; color: #999; margin-top: 2px; }
    .invoice-title { text-align: right; }
    .invoice-title h1 { font-size: 28px; color: #111; letter-spacing: 2px; text-transform: uppercase; }
    .invoice-title p { font-size: 12px; color: #666; margin-top: 4px; }
    .divider { border-top: 2px solid #e11d48; margin: 20px 0; }
    .thin-divider { border-top: 1px solid #f0f0f0; margin: 12px 0; }
    .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 25px; }
    .label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #999; font-weight: bold; margin-bottom: 6px; }
    .value { font-size: 13px; color: #111; line-height: 1.7; }
    .bold { font-weight: bold; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    thead { background: #111; color: white; }
    thead th { padding: 10px 12px; text-align: left; font-size: 12px; font-weight: 600; }
    tbody tr:nth-child(even) { background: #fafafa; }
    tbody td { padding: 10px 12px; border-bottom: 1px solid #f0f0f0; font-size: 13px; }
    .totals { margin-left: auto; width: 280px; }
    .total-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; color: #555; }
    .total-final { display: flex; justify-content: space-between; padding: 10px 12px; background: #e11d48; color: white; border-radius: 8px; font-weight: bold; font-size: 16px; margin-top: 8px; }
    .payment-info { background: #f8f9fa; border-radius: 8px; padding: 16px; margin-top: 25px; }
    .thank-you { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #f0f0f0; }
    .thank-you p { color: #999; font-size: 12px; }
    @media print {
      body { margin: 0; }
      .no-print { display: none !important; }
      .invoice { border: none; margin: 0; padding: 20px; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="text-align:center; padding:12px; background:#f9f9f9; border-bottom:1px solid #eee;">
    <button onclick="window.print()" style="background:#e11d48;color:white;border:none;padding:9px 22px;border-radius:6px;cursor:pointer;font-size:14px;margin-right:8px;">🖨️ Print / Save PDF</button>
    <button onclick="window.close()" style="background:#555;color:white;border:none;padding:9px 22px;border-radius:6px;cursor:pointer;font-size:14px;">✕ Close</button>
  </div>

  <div class="invoice">
    <div class="header">
      <div>
        <div class="brand-name">💕 CoupleCart</div>
        <div class="brand-tagline">Gifts crafted with love</div>
        <div style="margin-top:10px; font-size:12px; color:#666; line-height:1.7;">
          couplecart.in<br/>
          hello@couplecart.in<br/>
          Bangalore, Karnataka, India
        </div>
      </div>
      <div class="invoice-title">
        <h1>Invoice</h1>
        <p style="font-size:15px; font-weight:bold; color:#e11d48; margin-top:6px;">#${orderId}</p>
        <p>Date: ${date}</p>
        <p style="margin-top:8px; background:#dcfce7; color:#166534; padding:4px 10px; border-radius:20px; font-size:12px; font-weight:bold; display:inline-block;">
          ${order.paymentStatus === 'paid' ? '✓ PAID' : '💵 COD'}
        </p>
      </div>
    </div>

    <div class="divider"></div>

    <div class="two-col">
      <div>
        <div class="label">Bill To</div>
        <div class="value">
          <span class="bold">${addr.name || '—'}</span><br/>
          ${addr.address || ''}<br/>
          ${addr.city || ''}, ${addr.state || ''} — ${addr.pincode || ''}<br/>
          📞 ${addr.phone || '—'}<br/>
          ✉ ${order.userEmail || '—'}
        </div>
      </div>
      <div>
        <div class="label">Order Details</div>
        <div class="value">
          <strong>Order ID:</strong> #${orderId}<br/>
          <strong>Date:</strong> ${date}<br/>
          <strong>Payment:</strong> ${order.paymentStatus === 'paid' ? 'Online (Prepaid)' : 'Cash on Delivery'}<br/>
          ${order.paymentId ? `<strong>Payment ID:</strong> ${order.paymentId}<br/>` : ''}
          ${order.awbCode ? `<strong>Tracking:</strong> ${order.awbCode}` : ''}
        </div>
      </div>
    </div>

    <!-- Items Table -->
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Product</th>
          <th style="text-align:center">Qty</th>
          <th style="text-align:right">Unit Price</th>
          <th style="text-align:right">Total</th>
        </tr>
      </thead>
      <tbody>
        ${items.map((item, i) => `
          <tr>
            <td>${i + 1}</td>
            <td>${item.name}</td>
            <td style="text-align:center">${item.quantity}</td>
            <td style="text-align:right">₹${item.price?.toLocaleString('en-IN')}</td>
            <td style="text-align:right">₹${(item.price * item.quantity).toLocaleString('en-IN')}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <!-- Totals -->
    <div class="totals">
      <div class="total-row"><span>Subtotal</span><span>₹${subtotal.toLocaleString('en-IN')}</span></div>
      <div class="total-row"><span>Delivery</span><span>${delivery === 0 ? 'FREE' : `₹${delivery}`}</span></div>
      <div class="total-row"><span>Tax</span><span>Included</span></div>
      <div class="total-final"><span>Total</span><span>₹${total.toLocaleString('en-IN')}</span></div>
    </div>

    <!-- Payment note -->
    <div class="payment-info" style="margin-top:25px;">
      <div style="font-size:11px; font-weight:bold; text-transform:uppercase; letter-spacing:1px; color:#666; margin-bottom:6px;">Payment Information</div>
      <div style="font-size:13px; color:#333;">
        Status: <strong style="color:${order.paymentStatus === 'paid' ? '#166534' : '#854d0e'};">
          ${order.paymentStatus === 'paid' ? 'PAID — Thank you!' : 'Cash on Delivery'}
        </strong><br/>
        ${order.paymentId ? `Transaction ID: <span style="font-family:monospace">${order.paymentId}</span>` : ''}
      </div>
    </div>

    <div class="thank-you">
      <p style="font-size:16px; color:#e11d48; font-weight:bold; margin-bottom:6px;">Thank you for your order! 💕</p>
      <p>For any queries, contact us at hello@couplecart.in or WhatsApp +91 98765 43210</p>
      <p style="margin-top:6px;">Returns accepted within 7 days. Visit couplecart.in/policy for details.</p>
    </div>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=800,height=900');
  win.document.write(html);
  win.document.close();
}


// ── PACKING SLIP (simpler internal document) ──────────────────────
export function generatePackingSlip(order, merchantProfile) {
  const orderId = order.id?.slice(-8).toUpperCase() || 'XXXXXXXX';
  const items   = order.items || [];
  const addr    = order.shippingAddress || {};
  const date    = new Date().toLocaleDateString('en-IN');

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Packing Slip - ${orderId}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 13px; color: #111; }
    .slip { max-width: 600px; margin: 10px auto; padding: 20px; border: 1px solid #ddd; }
    h2 { color: #e11d48; margin-bottom: 15px; }
    .section { margin-bottom: 15px; }
    label { font-size: 11px; color: #666; text-transform: uppercase; font-weight: bold; display: block; margin-bottom: 3px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background: #f5f5f5; }
    .check { width: 20px; height: 20px; border: 2px solid #333; display: inline-block; }
    @media print { .no-print { display: none; } }
  </style>
</head>
<body>
  <div class="no-print" style="padding:10px; background:#f0f0f0; text-align:center; margin-bottom:10px;">
    <button onclick="window.print()" style="background:#e11d48;color:white;border:none;padding:8px 18px;border-radius:4px;cursor:pointer;">🖨️ Print Packing Slip</button>
  </div>
  <div class="slip">
    <h2>📦 Packing Slip — #${orderId}</h2>
    <div class="section">
      <label>Ship To</label>
      <strong>${addr.name}</strong> — ${addr.phone}<br/>
      ${addr.address}, ${addr.city}, ${addr.state} — ${addr.pincode}
    </div>
    <div class="section">
      <label>From</label>
      ${merchantProfile?.shopName || 'CoupleCart'} | Date: ${date}
    </div>
    <table>
      <thead><tr><th>✓</th><th>Product</th><th>Qty</th></tr></thead>
      <tbody>
        ${items.map((item) => `<tr><td><div class="check"></div></td><td>${item.name}</td><td>${item.quantity}</td></tr>`).join('')}
      </tbody>
    </table>
    <div style="margin-top:15px; padding:10px; background:#f9f9f9; border-radius:6px;">
      <strong>Payment:</strong> ${order.paymentStatus === 'paid' ? '✅ PREPAID — Do NOT collect cash' : '💵 COD — Collect ₹' + order.total?.toLocaleString('en-IN')}
    </div>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=700,height=600');
  win.document.write(html);
  win.document.close();
}
